import {
    type CommentTagCatalogue,
    SENTIMENT_EMOJI,
    SURVEY_COMMENT_SEARCH_MAX_LENGTH,
    type SurveyComment,
} from '@engagement-web-app/common';
import classNames from 'classnames';
import { useMemo, useState } from 'react';
import { ErrorOverlay } from '@/components/ErrorOverlay';
import { Flex } from '@/components/Flex';
import { Pill } from '@/components/Pill';
import { SearchInput } from '@/components/SearchInput';
import { Section, SectionHeader } from '@/components/Section';
import { Select } from '@/components/Select';
import { Skeleton } from '@/components/Skeleton';
import { Table } from '@/components/Table';
import { TextButton } from '@/components/TextButton';
import { hiddenInPrint, wrapTextInPrint } from '@/styles/print.css';
import type { SortOrder } from '@/utils';
import { TagFilter } from '../TagFilter';
import * as styles from './index.css';

type Question = {
    id: string;
    text: string;
};

/**
 * `rawAnswer` is present only on the consultant screens; the client-facing API
 * never returns the responder's text before masking, so the row simply omits it.
 */
type TableComment = SurveyComment & { readonly rawAnswer?: string };

type SurveyCommentsTableProps<T extends TableComment> = {
    /** Omitted for callers without the comment-edit permission; hides the edit column. */
    onEditComment?: (comment: T) => void;
    /**
     * `null` until the first fetch resolves. The underlying query keeps the
     * previous filter's/page's comments around during a refetch, so this
     * doesn't go back to `null` on a filter change — see `isFetching`.
     */
    comments: readonly T[] | null;
    /** `null` while still loading. */
    questions: readonly Question[] | null;
    selectedQuestionId: string;
    onSelectedQuestionIdChange: (id: string) => void;
    /** `null` while still loading. */
    tagCatalogue: CommentTagCatalogue | null;
    selectedTagIds: readonly string[];
    onSelectedTagIdsChange: (ids: string[]) => void;
    searchText: string;
    onSearchTextChange: (text: string) => void;
    /** True while `comments` is being (re)fetched for the current filter/page. */
    isFetching: boolean;
    isError: boolean;
    onRetry: () => void;
};

type SortKey = 'sentiment';

// Pure composition: `questions`/`tagCatalogue` and `comments` are independent
// loading states (the former load once, the latter reloads on every filter/page
// change), so each half buffers its own rather than one gate covering both.
export function SurveyCommentsTable<T extends TableComment>({
    onEditComment,
    comments,
    questions,
    selectedQuestionId,
    onSelectedQuestionIdChange,
    tagCatalogue,
    selectedTagIds,
    onSelectedTagIdsChange,
    searchText,
    onSearchTextChange,
    isFetching,
    isError,
    onRetry,
}: SurveyCommentsTableProps<T>) {
    if (isError) return <ErrorOverlay active onRetry={onRetry} message="コメントの取得に失敗しました。" />;

    return (
        <Section>
            <SectionHeader
                title="コメント"
                description="自由記述の回答を一覧し、内容から判定されたタグと感情を併せて表示します。設問やタグでの絞り込み、キーワード検索ができます。"
            />

            <Flex direction="column" gap="6">
                {questions && tagCatalogue ? (
                    <Filters
                        questions={questions}
                        selectedQuestionId={selectedQuestionId}
                        onSelectedQuestionIdChange={onSelectedQuestionIdChange}
                        tagCatalogue={tagCatalogue}
                        selectedTagIds={selectedTagIds}
                        onSelectedTagIdsChange={onSelectedTagIdsChange}
                        searchText={searchText}
                        onSearchTextChange={onSearchTextChange}
                    />
                ) : (
                    <FiltersSkeleton />
                )}

                {comments ? (
                    <CommentsTableContent comments={comments} isFetching={isFetching} onEditComment={onEditComment} />
                ) : (
                    <CommentsTableContentSkeleton />
                )}
            </Flex>
        </Section>
    );
}

type FiltersProps = {
    questions: readonly Question[];
    selectedQuestionId: string;
    onSelectedQuestionIdChange: (id: string) => void;
    tagCatalogue: CommentTagCatalogue;
    selectedTagIds: readonly string[];
    onSelectedTagIdsChange: (ids: string[]) => void;
    searchText: string;
    onSearchTextChange: (text: string) => void;
};

function Filters({
    questions,
    selectedQuestionId,
    onSelectedQuestionIdChange,
    tagCatalogue,
    selectedTagIds,
    onSelectedTagIdsChange,
    searchText,
    onSearchTextChange,
}: FiltersProps) {
    return (
        <>
            <Select
                className={classNames(styles.questionSelect, hiddenInPrint)}
                value={selectedQuestionId}
                onChange={(e) => onSelectedQuestionIdChange(e.target.value)}
            >
                <option value="">全ての設問</option>
                {questions.map((question) => (
                    <option key={question.id} value={question.id}>
                        {question.text}
                    </option>
                ))}
            </Select>

            <Flex align="center" justify="space-between" gap="4" className={hiddenInPrint}>
                <TagFilter
                    catalogue={tagCatalogue}
                    selectedTagIds={selectedTagIds}
                    onSelectedTagIdsChange={onSelectedTagIdsChange}
                />
                <SearchInput
                    placeholder="コメントを検索"
                    value={searchText}
                    onChange={(e) => onSearchTextChange(e.target.value)}
                    maxLength={SURVEY_COMMENT_SEARCH_MAX_LENGTH}
                />
            </Flex>
        </>
    );
}

function FiltersSkeleton() {
    return (
        <>
            <Skeleton.Box width="100%" height={42} />

            <Flex align="center" justify="space-between" gap="4">
                <Skeleton.Box width={110} height={36} />
                <Skeleton.Box width={240} height={36} />
            </Flex>
        </>
    );
}

type CommentsTableContentProps<T extends TableComment> = {
    comments: readonly T[];
    isFetching: boolean;
    onEditComment?: (comment: T) => void;
};

function CommentsTableContent<T extends TableComment>({
    comments,
    isFetching,
    onEditComment,
}: CommentsTableContentProps<T>) {
    const [sortKey, setSortKey] = useState<SortKey | null>(null);
    const [sortDir, setSortDir] = useState<SortOrder | null>(null);

    const sortedComments = useMemo(() => {
        if (sortKey === null || sortDir === null) return [...comments];

        return [...comments].sort((a, b) => {
            // TODO: Refine sorting once we have a proper enum in place
            const aVal = a.sentiment.charCodeAt(0);
            const bVal = b.sentiment.charCodeAt(0);
            return sortDir === 'desc' ? bVal - aVal : aVal - bVal;
        });
    }, [comments, sortKey, sortDir]);

    function handleSort(key: SortKey) {
        if (sortKey !== key) {
            setSortKey(key);
            setSortDir('desc');
        } else if (sortDir === 'desc') {
            setSortDir('asc');
        } else {
            setSortKey(null);
            setSortDir(null);
        }
    }

    function directionFor(key: SortKey) {
        return sortKey === key ? sortDir : null;
    }

    return (
        <>
            <Table.RowCount label="コメント数" count={comments.length} unit="件" />

            <Skeleton.Overlay active={isFetching}>
                <Table.Root>
                    <Table.Header>
                        <Table.HeaderCell>コメント</Table.HeaderCell>
                        <Table.HeaderCell>
                            <Table.SortButton
                                onClick={() => handleSort('sentiment')}
                                direction={directionFor('sentiment')}
                            >
                                感情
                            </Table.SortButton>
                        </Table.HeaderCell>
                        {onEditComment && <Table.HeaderCell>編集</Table.HeaderCell>}
                    </Table.Header>

                    <Table.Body>
                        {sortedComments.map((comment) => (
                            <Table.Row key={`${comment.answerId}:${comment.questionId}`}>
                                <Table.Cell>
                                    <div className={styles.commentCell}>
                                        {/* Implement tooltip to display full comment text */}
                                        <span
                                            title={comment.answer}
                                            className={classNames(styles.text, wrapTextInPrint)}
                                        >
                                            {comment.answer}
                                        </span>
                                        {comment.rawAnswer && (
                                            <span className={classNames(styles.rawAnswer, wrapTextInPrint)}>
                                                {comment.rawAnswer}
                                            </span>
                                        )}
                                        {comment.tags.length > 0 && (
                                            <div className={styles.tags}>
                                                {comment.tags.map((tag) => (
                                                    <Pill key={tag.id} variant="quinary" weight="bold" size="small">
                                                        {tag.name}
                                                    </Pill>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </Table.Cell>
                                <Table.Cell>
                                    <span className={styles.sentiment} title={comment.sentiment}>
                                        {SENTIMENT_EMOJI[comment.sentiment]}
                                    </span>
                                </Table.Cell>
                                {onEditComment && (
                                    <Table.Cell>
                                        <TextButton type="button" onClick={() => onEditComment(comment)}>
                                            編集
                                        </TextButton>
                                    </Table.Cell>
                                )}
                            </Table.Row>
                        ))}

                        {sortedComments.length === 0 && <Table.EmptyRow>表示するコメントがありません</Table.EmptyRow>}
                    </Table.Body>
                </Table.Root>
            </Skeleton.Overlay>
        </>
    );
}

function CommentsTableContentSkeleton() {
    return (
        <>
            <Skeleton.Line width={120} height="xs" />
            <Table.Skeleton columns={2} rows={4} />
        </>
    );
}

/**
 * A placeholder for the whole card, for the page's initial load — distinct from the
 * partial loading the component handles internally once it's on screen.
 */
export function SurveyCommentsTableSkeleton() {
    return (
        <Section>
            <Flex direction="column" gap="6">
                <Flex direction="column" gap="1">
                    <Skeleton.Line width="40%" height="xl" />
                    <Skeleton.Line width="70%" height="sm" />
                </Flex>

                <FiltersSkeleton />

                <CommentsTableContentSkeleton />
            </Flex>
        </Section>
    );
}
